import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';
import { portalApi } from '../lib/api';

interface PortalDocument {
  id: string;
  originalFilename: string;
  mimeType: string;
  fileSizeBytes: number;
  status: string;
  uploadSource: string;
  createdAt: string;
}

export function HistoryPage() {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState<PortalDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    portalApi
      .getDocuments()
      .then((res: { data: PortalDocument[] }) => setDocuments(res.data))
      .catch(() => setError(t('portal.uploadError')))
      .finally(() => setLoading(false));
  }, [t]);

  const statusLabel = (status: string) => {
    switch (status) {
      case 'NEW': return t('portal.status.received');
      case 'REVIEWING': return t('portal.status.reviewing');
      case 'REVIEWED':
      case 'POSTED': return t('portal.status.posted');
      default: return status;
    }
  };

  const statusColor = (status: string) => {
    switch (status) {
      case 'NEW': return 'status-new';
      case 'REVIEWING': return 'status-review';
      case 'REVIEWED':
      case 'POSTED': return 'status-ok';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="p-4">
      <h2 className="text-lg font-bold mb-4">{t('portal.history')}</h2>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse bg-muted rounded-lg h-16" />
          ))}
        </div>
      ) : error ? (
        <p className="text-sm text-destructive text-center py-8">{error}</p>
      ) : !documents.length ? (
        <div className="text-center py-8">
          <div className="w-14 h-14 rounded-2xl bg-muted/60 flex items-center justify-center mx-auto mb-4">
            <FileText className="h-7 w-7 text-muted-foreground/40" />
          </div>
          <p className="text-sm text-muted-foreground">{t('portal.noHistory')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {documents.map((doc) => (
            <div key={doc.id} className="bg-card rounded-2xl border border-border p-3 shadow-lifted hover:shadow-lifted-lg transition-shadow">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{doc.originalFilename}</p>
                  <p className="font-mono text-xs text-muted-foreground mt-0.5">
                    {new Date(doc.createdAt).toLocaleDateString('el-GR', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                    {' · '}
                    {(doc.fileSizeBytes / 1024).toFixed(0)} KB
                  </p>
                </div>
                <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold shrink-0 ${statusColor(doc.status)}`}>
                  {statusLabel(doc.status)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
